import { memo } from 'react';
import { useSecond } from './clock';
import { useI18n, type TKey } from '../../lib/i18n';
import {
  bandOf,
  elapsedSeconds,
  formatElapsed,
  lineProgress,
  ORDER_TYPE_DELIVERY,
  orderTypeLabel,
  pickName,
  sourceBadge,
  splitBilingual,
  targetProgress,
  type Band,
} from './helpers';
import type { KdsLine, KdsSettings, KdsTicket } from '../../types/kds';

// ─────────────────────────────────────────────────────────────────────────────
// One ticket.
//
// The card is memoised on the ticket object, and the only thing inside it that
// changes every second — the timer — is its own component subscribed to the
// shared clock. So a board of forty tickets does forty renders when data
// changes and forty *span* renders per second otherwise, rather than forty full
// card reconciliations per second. On the cheap Android tablets these run on,
// that is the difference between a board that scrolls and one that judders.
//
// Bilingual throughout: the chrome comes from the dictionary, the item names
// lead with the screen's language and fall back to the other script. The
// stylesheet uses logical properties, so the whole card mirrors under RTL with
// no conditional layout here.
// ─────────────────────────────────────────────────────────────────────────────

export interface TicketCardProps {
  ticket: KdsTicket;
  settings: KdsSettings | null;
  warnSeconds: number;
  criticalSeconds: number;
  skewMs: number;
  pending: boolean;
  /** Expo/assembly cards get the whole-order treatment and a SERVE button. */
  expo: boolean;
  /**
   * Show which station this ticket belongs to.
   *
   * True whenever the board spans more than one station. Without it, an order
   * that went to both the grill and the pass draws two identical-looking cards
   * with the same order number — which reads as the same ticket rendered twice
   * rather than as two stations' share of one order.
   */
  showStation?: boolean;
  onStart: (id: number) => void;
  onBump: (id: number) => void;
  onServe: (id: number) => void;
  onRecall: (id: number) => void;
  onLine: (ticketId: number, lineId: number, ready: boolean) => void;
  onPriority: (id: number, priority: 0 | 1 | 2) => void;
  onHold: (id: number, held: boolean) => void;
  onFireCourse: (orderId: number, courseNo: number) => void;
  /** Reprint — `kind` picks the kitchen slip or the customer receipt. */
  onPrint: (ticketId: number, kind: 'receipt' | 'kitchen') => void;
  /** Print keys in flight, so the pressed button can show it is working. */
  printing: Set<string>;
}

function TicketCardInner(props: TicketCardProps): JSX.Element {
  const { ticket, settings, warnSeconds, criticalSeconds, skewMs, pending, expo } = props;
  const showStation = props.showStation ?? expo;
  const { lang, t } = useI18n();

  const second = useSecond();
  const elapsed = elapsedSeconds(ticket.firedAt, skewMs, second * 1000);
  const band: Band =
    ticket.status === 'ready' || ticket.status === 'served'
      ? 'fresh'
      : bandOf(elapsed, warnSeconds, criticalSeconds);

  const progress = targetProgress(ticket, elapsed);
  const { ready, total } = lineProgress(ticket);
  const source = sourceBadge(ticket, t);
  const cancelled = ticket.status === 'cancelled';

  const classes = [
    'kds-card',
    `band-${band}`,
    `status-${ticket.status}`,
    ticket.priority > 0 ? 'is-rush' : '',
    ticket.isHeld ? 'is-held' : '',
    pending ? 'is-pending' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={classes} aria-label={`${ticket.orderCode ?? ticket.orderId}`}>
      <header className="kds-card-head">
        <div className="kds-card-ident">
          {/* bdi: an order code sitting next to Arabic text is a neutral-run
              that the bidi algorithm will otherwise reorder — "#16655" can end
              up rendered as "16655#" beside an Arabic label. */}
          <bdi className="kds-order-no">{ticket.orderCode ?? `#${ticket.orderId}`}</bdi>
          {/* The order's external number, when it has one — the code the
              customer and the ordering platform's support chat both quote, and
              which the POS entry number means nothing to. Rendered only when
              there is a real value: most till sales have none, and an empty
              chip beside every order number would be noise on every card.
              bdi for the same reason as the order number: a Latin code sitting
              in an Arabic run gets reordered without it. */}
          {ticket.referenceNo && (
            <bdi className="kds-chip kds-chip-ref" title={ticket.referenceNo}>
              {ticket.referenceNo}
            </bdi>
          )}
          <span className="kds-order-type">{orderTypeLabel(ticket, t)}</span>
          {ticket.tableLabel && (
            <bdi className="kds-chip kds-chip-table">{ticket.tableLabel}</bdi>
          )}
          {source && <span className="kds-chip kds-chip-source">{source}</span>}
          {showStation && ticket.stationName && (
            <bdi
              className="kds-chip kds-chip-station"
              style={
                ticket.stationColor
                  ? ({ ['--chip']: ticket.stationColor } as React.CSSProperties)
                  : undefined
              }
            >
              {ticket.stationName}
            </bdi>
          )}
          {ticket.courseNo > 1 && <span className="kds-chip">C{ticket.courseNo}</span>}
          {ticket.recallCount > 0 && (
            <span className="kds-chip kds-chip-recall" title={t('recalled')}>
              ↺{ticket.recallCount}
            </span>
          )}
        </div>
        <Timer firedAt={ticket.firedAt} skewMs={skewMs} frozen={ticket.readyAt} band={band} />
      </header>

      {settings?.showCustomer !== false && ticket.customerName ? (
        <div className="kds-card-sub">
          <bdi className="kds-customer">
            {pickName(lang, ticket.customerName, ticket.customerNameAr)}
          </bdi>
        </div>
      ) : null}

      {progress != null && ticket.status !== 'ready' && (
        <div className="kds-target" aria-hidden="true">
          {/* Capped at 100% width; the bar turns red past target rather than
              overflowing, so a very late ticket still reads as one bar. */}
          <span
            className={progress > 1 ? 'over' : ''}
            style={{ width: `${Math.min(100, progress * 100)}%` }}
          />
        </div>
      )}

      <ul className="kds-lines">
        {ticket.lines.map((line) => (
          <LineRow
            key={line.id}
            line={line}
            lang={lang}
            expo={expo}
            onToggle={() => props.onLine(ticket.id, line.id, line.status !== 'ready')}
          />
        ))}
      </ul>

      {settings?.showOrderNotes !== false && ticket.notes && (
        <p className="kds-order-note">{ticket.notes}</p>
      )}

      <footer className="kds-card-actions">
        {cancelled ? (
          <span className="kds-cancelled-banner">{t('cancelled')}</span>
        ) : ticket.isHeld ? (
          <>
            <span className="kds-held-banner">
              {t('held')} · {t('course')} {ticket.courseNo}
            </span>
            <button
              type="button"
              className="kds-btn kds-btn-primary"
              onClick={() => props.onFireCourse(ticket.orderId, ticket.courseNo)}
            >
              {t('fire')}
            </button>
          </>
        ) : (
          <>
            <span className="kds-progress-count">
              {ready}/{total}
            </span>

            {/* Reprint. The kitchen slip is on every card because a lost slip
                is the commonest thing that goes wrong at a pass; the customer
                receipt is only on expo, where the bag is being made up and the
                paper actually travels with it. Icons, not words: the footer is
                already the busiest row on the card. */}
            <PrintButton
              icon="🖨"
              label={t('printKitchen')}
              busy={props.printing.has(`${ticket.id}:kitchen`)}
              onClick={() => props.onPrint(ticket.id, 'kitchen')}
            />
            {expo && (
              <PrintButton
                icon="🧾"
                label={t('printReceipt')}
                busy={props.printing.has(`${ticket.id}:receipt`)}
                onClick={() => props.onPrint(ticket.id, 'receipt')}
              />
            )}

            {/* Rush is one tap that toggles. A kitchen needs a single gesture
                for "this one first", not a context menu. */}
            <button
              type="button"
              className={`kds-btn kds-btn-ghost ${ticket.priority > 0 ? 'active' : ''}`}
              onClick={() => props.onPriority(ticket.id, ticket.priority > 0 ? 0 : 1)}
              title={ticket.priority > 0 ? t('clearRush') : t('rush')}
              aria-label={ticket.priority > 0 ? t('clearRush') : t('rush')}
              aria-pressed={ticket.priority > 0}
            >
              ⚡
            </button>

            {ticket.status === 'ready' || ticket.status === 'served' ? (
              <button
                type="button"
                className="kds-btn kds-btn-warn"
                onClick={() => props.onRecall(ticket.id)}
              >
                {t('recall')}
              </button>
            ) : (
              settings?.requireStart &&
              ticket.status === 'queued' && (
                <button
                  type="button"
                  className="kds-btn kds-btn-ghost"
                  onClick={() => props.onStart(ticket.id)}
                >
                  {t('start')}
                </button>
              )
            )}

            {expo ? (
              <button
                type="button"
                className="kds-btn kds-btn-primary"
                disabled={ticket.status === 'served'}
                onClick={() => props.onServe(ticket.id)}
              >
                {/* An ellipsis on a delivery order, because that tap opens the
                    driver dialog rather than clearing the card outright — the
                    ordinary affordance for "a question follows". */}
                {ticket.status === 'served'
                  ? t('served')
                  : ticket.orderType === ORDER_TYPE_DELIVERY
                    ? `${t('serve')}…`
                    : t('serve')}
              </button>
            ) : (
              ticket.status !== 'ready' && (
                <button
                  type="button"
                  className="kds-btn kds-btn-primary"
                  onClick={() => props.onBump(ticket.id)}
                >
                  {t('bump')}
                </button>
              )
            )}
          </>
        )}
      </footer>

      {expo && ticket.stationProgress && ticket.stationProgress.length > 0 && (
        <div className="kds-station-progress">
          {ticket.stationProgress.map((sp) => (
            <span
              key={sp.stationId}
              className={`kds-sp ${sp.status === 'ready' || sp.status === 'served' ? 'done' : ''}`}
              style={sp.stationColor ? { borderColor: sp.stationColor } : undefined}
              title={`${sp.stationName ?? ''}: ${sp.readyLines}/${sp.totalLines}`}
            >
              {sp.stationName}
              <b>
                {sp.readyLines}/{sp.totalLines}
              </b>
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * Re-render only when something the card draws has changed.
 *
 * The timer is excluded from this comparison on purpose — it lives in its own
 * subscriber below, so the card body genuinely does not need to re-render when
 * the second changes. Comparing `ticket` by reference is safe because the store
 * always replaces the whole ticket object on any update.
 *
 * Note there is no language in the comparison: `useI18n` is a subscription, so
 * a language change re-renders every card through React's own path without the
 * memo having to know about it.
 */
export const TicketCard = memo(TicketCardInner, (a, b) => {
  return (
    a.ticket === b.ticket &&
    a.settings === b.settings &&
    a.pending === b.pending &&
    a.expo === b.expo &&
    a.showStation === b.showStation &&
    a.skewMs === b.skewMs &&
    a.warnSeconds === b.warnSeconds &&
    a.criticalSeconds === b.criticalSeconds &&
    // The store swaps the whole Set on every change, so this is a reference
    // check. It re-renders the board when a print starts or finishes, which is
    // rare and is the only way the pressed button can show it is working.
    a.printing === b.printing
  );
});

/**
 * A reprint button.
 *
 * Disabled while its own request is in flight — the one thing a cook will
 * reliably do to a button that appears to have done nothing is press it again,
 * and each press is another sheet of paper.
 */
function PrintButton(props: {
  icon: string;
  label: string;
  busy: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`kds-btn kds-btn-ghost ${props.busy ? 'is-busy' : ''}`}
      onClick={props.onClick}
      disabled={props.busy}
      title={props.label}
      aria-label={props.label}
    >
      {props.busy ? '…' : props.icon}
    </button>
  );
}

// ── Timer ───────────────────────────────────────────────────────────────────

/**
 * The only thing on the board that ticks.
 *
 * Freezes at the bump time once the ticket is ready: a completed ticket whose
 * timer keeps climbing tells the cook they are still late for something they
 * already finished.
 */
function Timer(props: {
  firedAt: string;
  skewMs: number;
  frozen: string | null;
  band: Band;
}): JSX.Element {
  const second = useSecond();
  const stop = props.frozen ? new Date(props.frozen).getTime() : second * 1000;
  const elapsed = elapsedSeconds(props.firedAt, props.skewMs, stop);
  return (
    <span className={`kds-timer band-${props.band} ${props.frozen ? 'frozen' : ''}`}>
      {formatElapsed(elapsed)}
    </span>
  );
}

// ── Line ────────────────────────────────────────────────────────────────────

const LineRow = memo(function LineRow(props: {
  line: KdsLine;
  lang: string;
  expo: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { line, lang } = props;
  const classes = ['kds-line', `line-${line.status}`, line.isVoided ? 'is-void' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes}>
      <button
        type="button"
        className="kds-line-hit"
        onClick={props.onToggle}
        disabled={line.isVoided}
        aria-pressed={line.status === 'ready'}
      >
        <span className="kds-qty">{formatQty(line.qty)}</span>
        <span className="kds-line-body">
          <bdi className="kds-item-name">{pickName(lang, line.name, line.nameAr)}</bdi>
          {/*
            One TAG per chosen option, not one row per group with the options
            run together by separators. A cook reads a card at a glance across
            a counter: "حجم صغير · ملفوف · بدون بصل" on one line is a sentence
            to parse, while three bordered tags are three things to pick up.
            Each tag carries its own group name, so an option that wraps onto
            the next row is still self-describing.
          */}
          {(line.modifiers.length > 0 || (line.components?.length ?? 0) > 0) && (
            <span className="kds-mods">
              {line.modifiers.map((m, i) => {
                // The ERP stores both languages in ONE title with no separator,
                // so the tag showed "ملفوف Malfof" — every option twice.
                //
                // The API now splits it and sends both halves, so PREFER those.
                // The client-side split stays as the fallback because a
                // ticket's modifiers are frozen into `kds_ticket_line.modifiers`
                // when it is FIRED: everything already on the board was stored
                // as the merged string and will never be re-split server-side.
                // NB the fallback must split BOTH halves: when `nameAr` is
                // absent, `name` is still the merged string, so trusting it as
                // the English side would show both languages again.
                const g = m.group?.trim()
                  ? m.groupAr
                    ? { en: m.group, ar: m.groupAr }
                    : splitBilingual(m.group)
                  : null;
                const n = m.nameAr ? { en: m.name, ar: m.nameAr } : splitBilingual(m.name);
                return (
                  <bdi className="kds-mod" key={`${m.group ?? ''}-${m.name}-${i}`}>
                    {g && <span className="kds-mod-group">{pickName(lang, g.en, g.ar)}</span>}
                    <span className="kds-mod-name">{pickName(lang, n.en, n.ar)}</span>
                  </bdi>
                );
              })}
              {/*
                The components of a finished product ("Mahashi Mix" → onion,
                kosa, vine leaves …), after the chosen options and in the same
                tag. Names only, in the screen's language: the item master
                carries both scripts, so there is no bilingual title to split.
              */}
              {(line.components ?? []).map((c, i) => (
                <bdi className="kds-mod" key={`comp-${c.name}-${i}`}>
                  <span className="kds-mod-name">{pickName(lang, c.name, c.nameAr ?? null)}</span>
                </bdi>
              ))}
            </span>
          )}
          {line.notes && <bdi className="kds-line-note">{line.notes}</bdi>}
          {props.expo && line.stationName && (
            <span className="kds-line-station">{line.stationName}</span>
          )}
        </span>
        {line.status === 'ready' && (
          <span className="kds-tick" aria-hidden="true">
            ✓
          </span>
        )}
      </button>
    </li>
  );
});

/**
 * 1 → "1", 1.5 → "1.5". Kitchen quantities are usually whole, and a trailing
 * ".000" on every line is noise on a screen read from two metres away.
 */
function formatQty(qty: number): string {
  return Number.isInteger(qty) ? String(qty) : String(Number(qty.toFixed(3)));
}

/** Exported for the tests, which assert the bidi-safe formatting. */
export const __test = { formatQty };

export type { TKey };
